/* eslint-disable @typescript-eslint/no-explicit-any */
import { axios } from "../../lib/axios";
import { ApiError } from "../../types/ApiError";

// Admin-scoped view of one attempt. The student-scoped
// `/student/ifrs/:ifrsId/attempt/:studentToIfrsId/answers` refuses an admin
// token; this route carries the same data plus the exam it belongs to.

/** What a student actually submitted into one response slot. */
export interface IFRSResponseOptionAnswer {
  id: string;
  answerFilePath: string | null;
  // Same thing under the name the student-facing answers route uses.
  answerFileUrl?: string | null;
  answerData: { type: string; data: any } | null;
  studentId: string;
  createdAt: string;
  updatedAt: string;
  responseOptionId: string;
  // Which attempt this answer belongs to. The API returns every attempt's
  // answers in one list, so callers must filter on this.
  studentToIfrsId: string;
  student?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    img_url: string | null;
  };
}

/**
 * A slot the student answers into. `answerFileUrl` and `responseData` are the
 * blank template handed to them, not their work.
 */
export interface IFRSResponseOption {
  id: string;
  fileType: string;
  answerFileUrl: string | null;
  responseData: Record<string, any[][]> | null;
  ifrsQuestionId: string;
  ifrsResponseOptionAnswers: IFRSResponseOptionAnswer[];
}

export interface IFRSDetailExhibit {
  id: string;
  order: number;
  title: string;
  type: string;
  filePath: string;
  exhibitData: Record<string, any[][]> | null;
  questionId: string;
}

export interface IFRSDetailQuestion {
  id: string;
  status: string;
  description: string | null;
  marks: number | null;
  requirements: string | null;
  responseOptions: IFRSResponseOption[];
  exhibits: IFRSDetailExhibit[];
  IfrsMarks: any[];
}

export interface IFRSAttemptDetail {
  studentExamAttempt: {
    id: string;
    submittedAt: string | null;
    createdAt: string;
    updatedAt: string;
    student: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      img_url: string | null;
      phone: string | null;
      country: string | null;
    };
    totalMarks: number | null;
    marksBreakdown: any[];
  };
  ifrsExam: {
    id: string;
    title: string;
    status: string;
    duration: number;
    modelAnswerUrl: string | null;
    ifrsQuestions: IFRSDetailQuestion[];
  };
}

type GetIFRSAttemptDetailAPIResponse = {
  error?: ApiError | null;
  data?: IFRSAttemptDetail;
};

export const GetIFRSAttemptDetail = async (
  ifrsId: string,
  studentToIfrsId: string
): Promise<GetIFRSAttemptDetailAPIResponse> => {
  try {
    const response = await axios.get(
      `/ifrs/${ifrsId}/student/${studentToIfrsId}/ifrs-exams`
    );
    return { data: response.data?.data };
  } catch (error: any) {
    console.error("API Error:", error);
    if (error.response) {
      const apiError: ApiError = {
        statusCode: error.response.status,
        message: error.response.data?.message,
        error: error.response.data?.error,
      };
      return { error: apiError };
    } else {
      const fallbackError: ApiError = {
        statusCode: 500,
        message: "An unexpected error occurred",
      };
      return { error: fallbackError };
    }
  }
};
